import React, { useState, useEffect } from "react";
import DateFormat from "../Date";
import { Tooltip } from "react-tooltip";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";
import {
  TiBatteryLow,
  TiBatteryMid,
  TiBatteryHigh,
  TiBatteryFull,
} from "react-icons/ti";
import {
  BiSignal1,
  BiSignal2,
  BiSignal3,
  BiSignal4,
  BiSignal5,
} from "react-icons/bi";
import { CiWarning } from "react-icons/ci";
import { MdMoving } from "react-icons/md";
import { FaSnowflake } from "react-icons/fa";
import { IoMdFlame } from "react-icons/io";
import styles from "./style.module.scss";
import { OneTyreProps } from "../../../../types";
import { frontColours, rearColours } from "../../../../assets/tyreColours";
import runTimeVars from "../../../../runTimeVars";

function batteryIcon(pct: number | null, VBat: number | null) {
  if (pct == null) return <></>;

  let BatteryIcon;
  let style;

  if (pct < 40) {
    BatteryIcon = TiBatteryLow;
    style = styles.lowBattery;
  } else if (pct < 50) {
    BatteryIcon = TiBatteryMid;
    style = styles.mediumBattery;
  } else if (pct < 90) {
    BatteryIcon = TiBatteryHigh;
    style = styles.highBattery;
  } else {
    BatteryIcon = TiBatteryFull;
    style = styles.highBattery;
  }

  const toolTipContent = `${pct}%\n${VBat ? `${VBat}V` : ""}`;

  return (
    <>
      <BatteryIcon
        className={`${styles.largeIcon} ${styles.icon} ${styles.highBattery} ${style}`}
        data-tooltip-id="tpmsBattery"
        data-tooltip-content={toolTipContent}
      />
    </>
  );
}

function rssiIcon(rssi: number | null) {
  if (rssi == null) return <></>;

  let RSSIIcon;

  if (rssi > -65) {
    RSSIIcon = BiSignal5;
  } else if (rssi > -72) {
    RSSIIcon = BiSignal4;
  } else if (rssi > -80) {
    RSSIIcon = BiSignal3;
  } else if (rssi > -87) {
    RSSIIcon = BiSignal2;
  } else {
    RSSIIcon = BiSignal1;
  }

  const toolTipContent = `${rssi} dBm`;

  return (
    <>
      <RSSIIcon
        className={`${styles.largeIcon} ${styles.icon}`}
        data-tooltip-id="tpmsRSSI"
        data-tooltip-content={toolTipContent}
      />
    </>
  );
}

function relativePressure(pAbs: number | null, pAtm: number) {
  if (pAbs) {
    // const Math.round((pAbs / 1000 - 1) * 1000) / 1000;
    return (pAbs / 1000 - pAtm).toFixed(3);
  }
  return null;
}

function powerModeIcon(state: string | null) {
  if (state == null) return <></>;

  let PowerModeIcon;
  let toolTipContent = "";
  switch (state) {
    case "0":
      //Inflated, no movement, <= 50 deg
      //10s
      //AT, BT, P, RH
      PowerModeIcon = FaSnowflake;
      toolTipContent = "Stationary Cold";
      break;
    case "1":
      //Inflated, no movement, >50 deg
      //10s
      //AT, BT, P, RH, RT, TT
      PowerModeIcon = IoMdFlame;
      toolTipContent = "Stationary Hot";
      break;
    case "2":
      //Inflated, moving, > 50 deg
      //1s
      //AT, BT, P, RH, RT, TT
      PowerModeIcon = MdMoving;
      toolTipContent = "Moving";
      break;
    case "3":
      //Puncture
      //0.5s
      //AT, BT, P, RH, RT, TT
      PowerModeIcon = CiWarning;
      toolTipContent = "Transient";
      break;
    default:
      return <></>;
  }

  return (
    <>
      <PowerModeIcon
        className={`${styles.mediumIcon} ${styles.icon}`}
        data-tooltip-id="tpmsPowerMode"
        data-tooltip-content={toolTipContent}
      />
    </>
  );
}

export default function OneTyre(
  props: OneTyreProps & { selectMode?: boolean },
) {
  const [currentTime, setTime] = useState(Date.now());
  const { index, group, car, selectMode } = props;
  const tyre = useSelector(
    (state: RootState) =>
      state.tpms.activeTyreData[group === "Front" ? "front" : "rear"][index],
  );
  const pAtm =
    useSelector((state: RootState) => state.tpms.tpmsConfig?.pAtm) || 0;
  const sensorData = tyre.sensorData;
  const tyreId = tyre.tyreId;

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
  );

  const colours = group === "Front" ? frontColours : rearColours;

  const editModeState = useSelector(
    (state: RootState) => state.tpms.editModeState,
  );

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sensorData.SN, disabled: !editModeState });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  let tyreColour = null;
  if (index < colours.length) {
    tyreColour = colours[index];
  }

  let dateString;
  if (sensorData.dt) {
    dateString = new Date(sensorData.dt).toISOString();
  } else {
    dateString = null;
  }

  const dateDelta = (currentTime - (sensorData.dt ? sensorData.dt : 0)) / 1000;
  const SENSOR_TIMEOUT_SECONDS = 60; //render the sensor outdated after this many seconds
  const SENSOR_TIMEOUT_WARNING = 60 * 30; //orange warning after this time
  const SENSOR_TIMEOUT_CRITICAL = 60 * 60; //red warning after this time

  let isCarValid;
  if (runTimeVars.disableCarIdValidation === "true") {
    isCarValid = true;
  } else {
    isCarValid =
      (car === jagDrivers.redCar && sensorData.carId === "1") ||
      (car === jagDrivers.blueCar && sensorData.carId === "0") ||
      (car === "DEV" && sensorData.carId === "0");
  }

  const srDa = sensorData;
  const avgIR =
    srDa.TIR2 && srDa.TIR3 && srDa.TIR4
      ? (srDa.TIR2 + srDa.TIR3 + srDa.TIR4) / 3
      : srDa.TIR3;

  return (
    <>
      {/* Tooltips must be injected alongside the root element to work properly*/}
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div
          className={`${styles.container} ${
            editModeState ? styles.isEditable : null
          } ${dateDelta > SENSOR_TIMEOUT_SECONDS ? styles.outdated : null} ${
            !isCarValid ? styles.invalidCarId : null
          } ${selectMode ? styles.frozen : ""}`}
          style={tyreColour ? { borderColor: tyreColour } : {}}
        >
          <div className={styles.centerContainer}>
            <div className={styles.upperMiddle}>
              {sensorData.TAir ? `TAir: ${sensorData.TAir} °C` : null}
            </div>
            <div className={styles.middleMiddle}>
              {sensorData.pAbs
                ? `${relativePressure(sensorData.pAbs, pAtm)} bar`
                : null}
            </div>
            <div className={styles.lowerMiddle}>
              {avgIR ? `TTyre: ${avgIR.toFixed(1)} °C` : null}
            </div>
            <div className={styles.lowerLowerMiddle}>
              {sensorData.RH ? `RH: ${sensorData.RH}%` : null}
            </div>
          </div>
          <div
            className={styles.topLeft}
            data-tooltip-id="tpmsCorner"
            data-tooltip-content={sensorData.srPos ?? undefined}
          >
            {tyreId}
          </div>
          <div className={styles.topMiddle}>{sensorData.SN}</div>
          <div className={styles.topRight}>
            {batteryIcon(sensorData.pctBat, sensorData.VBat)}
          </div>
          <div className={styles.bottomLeft}>
            {powerModeIcon(sensorData.pwrMode)}
          </div>
          <div
            className={`${styles.bottomMiddle} ${
              sensorData.dt && dateDelta > SENSOR_TIMEOUT_CRITICAL
                ? styles.outdatedCritical
                : sensorData.dt && dateDelta > SENSOR_TIMEOUT_WARNING
                ? styles.outdatedWarning
                : null
            }`}
          >
            <DateFormat dateString={dateString}></DateFormat>
          </div>
          <div className={styles.bottomRight}>{rssiIcon(sensorData.RSSI)}</div>
        </div>
      </div>
      <Tooltip id="tpmsBattery" />
      <Tooltip id="tpmsPowerMode" />
      <Tooltip id="tpmsRSSI" />
      <Tooltip id="tpmsCorner" />
    </>
  );
}
